import { useCallback, useEffect, useRef, useState } from "react";
import { apiCall } from "store/services/service";
import { URL_SP_PARTICIPANTS, URL_HRM_EMPLOYEE_PARTICIPANTS, IMAGE_URL } from "store/services/URL";
import { USER_ROLE } from "Constants/constant";
import { roleAccess } from "helpers/RolesPermission";
import { isRequestCancelled } from "Utils/requestCancel";

/**
 * Custom hook for async participant search with incremental pagination
 * Loads 10 records initially, then +10 more on each scroll
 * - SP Admin/Manager: uses sp/participants-list (POST)
 * - Worker Portal: uses hrm/employee-participants (GET)
 */
export const useParticipantSearch = () => {
  // Constant per_page value - always load 10 at a time
  const PER_PAGE = 10;
  const isWorkerPortal = !roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]);

  // State
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);

  // Refs
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const loadedSearchTermRef = useRef("");
  const isInitialLoadRef = useRef(true);

  /**
   * Fetch participants from API
   */
  const fetchParticipants = useCallback(
    async (search = "", page = 1, append = false) => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const requestId = ++requestIdRef.current;
      const signal = abortControllerRef.current.signal;

      setIsLoading(true);

      const params = {
        page,
        per_page: PER_PAGE,
        search: search || "",
        sort_order: "desc",
        sort_field: "created_at",
      };

      try {
        const response = isWorkerPortal
          ? await apiCall.get({
              url: URL_HRM_EMPLOYEE_PARTICIPANTS,
              isAuthToken: true,
              params,
              signal,
            })
          : await apiCall.post({
              url: URL_SP_PARTICIPANTS,
              isAuthToken: true,
              params,
              bodyParams: { status: ["active"] },
              signal,
            });

        if (requestId !== requestIdRef.current) return;

        if (response?.data) {
          // Try multiple possible response structures (SP vs HRM worker portal)
          let participantsData, total, currentPageNum, lastPage;
          const participantPayload =
            response.data.participant ??
            response.data.data?.participant;

          if (participantPayload) {
            // Structure: .participant.data (SP) or .data.participant (HRM)
            participantsData = participantPayload.data || [];
            total = participantPayload.total || 0;
            currentPageNum = participantPayload.current_page || page;
            lastPage = participantPayload.total_pages || participantPayload.last_page || 1;
          } else if (response.data.data) {
            // Structure: response.data.data as array
            participantsData = Array.isArray(response.data.data) ? response.data.data : [];
            total = response.data.total || 0;
            currentPageNum = response.data.current_page || page;
            lastPage = response.data.total_pages || response.data.last_page || 1;
          } else {
            // Fallback: response.data is array
            participantsData = Array.isArray(response.data) ? response.data : [];
            total = participantsData.length;
            currentPageNum = page;
            lastPage = 1;
          }

          // Only show active participants (client-side safeguard if API returns inactive)
          const activeParticipants = participantsData.filter(
            (p) => p.status === "active"
          );

          // Transform to option format - create new objects to avoid cross-origin issues
          const newOptions = activeParticipants.map((p) => {
            // If profile_picture exists embed with IMAGE_URL; otherwise dropdown shows name avatar
            const rawPic = p.profile_picture ? String(p.profile_picture).trim() : "";
            const profile_picture =
              rawPic === ""
                ? ""
                : rawPic.startsWith("http")
                  ? rawPic
                  : `${IMAGE_URL}${rawPic}`;
            return {
              id: p.id ? Number(p.id) : 0,
              value: p.uuid ? String(p.uuid) : String(p.id),
              label: `${p.first_name || ""} ${p.last_name || ""}`.trim(),
              uuid: p.uuid ? String(p.uuid) : String(p.id),
              first_name: p.first_name ? String(p.first_name) : "",
              last_name: p.last_name ? String(p.last_name) : "",
              profile_picture,
            };
          });

          setOptions((prevOptions) => {
            if (append) {
              // Remove duplicates by value
              const existingValues = new Set(prevOptions.map((opt) => opt.value));
              const uniqueNewOptions = newOptions.filter(
                (opt) => !existingValues.has(opt.value)
              );
              return [...prevOptions, ...uniqueNewOptions];
            }
            return newOptions;
          });

          setTotalRecords(total);
          const calculatedHasMore = currentPageNum < lastPage;
          setHasMore(calculatedHasMore);
        } else {
          console.error('❌ No response.data found:', response);
        }
      } catch (error) {
        if (isRequestCancelled(error) || requestId !== requestIdRef.current) {
          return;
        }
        console.error("Error fetching participants:", error);
        // Don't clear options on error, keep showing previous results
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [isWorkerPortal]
  );

  /**
   * Initial load on mount
   */
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      fetchParticipants("", 1, false);
    }
  }, [fetchParticipants]);

  /**
   * Debounced search handler
   */
  const handleInputChange = useCallback((inputValue) => {
    setSearchInputValue(inputValue);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      // Reset to page 1 on new search
      setCurrentPage(1);
      loadedSearchTermRef.current = inputValue;

      // Fetch with new search term from page 1
      fetchParticipants(inputValue, 1, false);
    }, 500);
  }, [fetchParticipants]);

  /**
   * Handle scroll to bottom - load next page (+10 records)
   */
  const handleMenuScrollToBottom = useCallback(() => {
    if (isLoading || !hasMore) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);

    // Fetch next page and append
    fetchParticipants(loadedSearchTermRef.current, nextPage, true);
  }, [isLoading, hasMore, currentPage, fetchParticipants]);

  /**
   * Reset to default list (first page, no search). Call when dropdown opens so it shows default values.
   */
  const resetToDefault = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setSearchInputValue("");
    setCurrentPage(1);
    loadedSearchTermRef.current = "";
    fetchParticipants("", 1, false);
  }, [fetchParticipants]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      requestIdRef.current += 1;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    options,
    isLoading,
    hasMore,
    searchInputValue,
    totalRecords,
    handleInputChange,
    handleMenuScrollToBottom,
    resetToDefault,
  };
};
